/**
 * Lesson Configuration Builder
 * 드래그 앤 드롭으로 학습 순서를 설정하는 UI
 */
import { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { RotateCcw, Eye } from 'lucide-react'
import type { LessonConfiguration, ComponentConfig, ConfigurationOverrides } from '@/types/configuration'
import { createDefaultConfiguration } from '@/types/configuration'
import { ComponentItem } from './ComponentItem'
import { CompletionRulesEditor } from './CompletionRulesEditor'
import { LearningSettingsEditor } from './LearningSettingsEditor'
import { ProductLearningConfigEditor } from './ProductLearningConfigEditor'
import { ModuleLearningConfigEditor } from './ModuleLearningConfigEditor'

interface ConfigurationBuilderProps {
  initialConfiguration?: LessonConfiguration
  onChange: (configuration: LessonConfiguration) => void
  onCancel?: () => void
  showPreview?: boolean
}

export const ConfigurationBuilder: React.FC<ConfigurationBuilderProps> = ({
  initialConfiguration,
  onChange,
  onCancel,
  showPreview = true
}) => {
  const [configuration, setConfiguration] = useState<LessonConfiguration>(
    initialConfiguration || createDefaultConfiguration()
  )
  // overrides 업데이트 헬퍼
  const handleOverridesUpdate = useCallback((updater: (prev: ConfigurationOverrides) => ConfigurationOverrides) => {
    setConfiguration(prev => ({
      ...prev,
      overrides: updater(prev.overrides || {})
    }))
  }, [setConfiguration])

  const [activeTab, setActiveTab] = useState<'sequence' | 'rules' | 'settings' | 'product' | 'module'>('rules')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  // configuration 변경 시 부모에 알림
  useEffect(() => {
    onChange(configuration)
  }, [configuration, onChange])

  // 컴포넌트 업데이트
  const handleUpdateComponent = useCallback((index: number, component: ComponentConfig) => {
    setConfiguration(prev => ({
      ...prev,
      sequence: prev.sequence.map((c, i) => (i === index ? component : c))
    }))
  }, [])

  // 순서 위로 이동
  const handleMoveUp = useCallback((index: number) => {
    if (index === 0) return
    setConfiguration(prev => {
      const newSequence = [...prev.sequence]
      ;[newSequence[index - 1], newSequence[index]] = [newSequence[index], newSequence[index - 1]]
      return { ...prev, sequence: newSequence }
    })
  }, [])

  // 순서 아래로 이동
  const handleMoveDown = useCallback((index: number) => {
    setConfiguration(prev => {
      if (index === prev.sequence.length - 1) return prev
      const newSequence = [...prev.sequence]
      ;[newSequence[index], newSequence[index + 1]] = [newSequence[index + 1], newSequence[index]]
      return { ...prev, sequence: newSequence }
    })
  }, [])

  // 드래그 시작
  const handleDragStart = useCallback((_e: React.DragEvent, index: number) => {
    setDragIndex(index)
  }, [])

  // 드래그 오버
  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }, [])

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setConfiguration(prev => {
        const newSequence = [...prev.sequence]
        const [removed] = newSequence.splice(dragIndex, 1)
        newSequence.splice(dragOverIndex, 0, removed)
        return { ...prev, sequence: newSequence }
      })
    }
    setDragIndex(null)
    setDragOverIndex(null)
  }, [dragIndex, dragOverIndex])

  // 초기화
  const handleReset = () => {
    setConfiguration(initialConfiguration || createDefaultConfiguration())
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">학습진행 설정</h3>
        <div className="flex items-center gap-2">
          {showPreview && (
            <button
              onClick={() => setShowPreviewModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Eye className="w-4 h-4" />
              미리보기
            </button>
          )}
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            초기화
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('rules')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'rules'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          학습 진행
        </button>
        <button
          onClick={() => setActiveTab('sequence')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'sequence'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          레슨 콤포넌트
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'settings'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          표시 및 기능
        </button>
        <button
          onClick={() => setActiveTab('product')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'product'
              ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          상품 레벨
        </button>
        <button
          onClick={() => setActiveTab('module')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'module'
              ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          모듈 레벨
        </button>
      </div>

      {/* 컨텐츠 */}
      <div className="p-6">
        {/* 레슨 콤포넌트 탭 */}
        {activeTab === 'sequence' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              드래그하거나 화살표 버튼으로 학습 순서를 변경할 수 있습니다.
            </p>
            <div className="flex items-center gap-4 mb-4 p-3 bg-purple-50 border border-purple-100 rounded-lg">
              <span className="text-sm font-medium text-purple-800">하위 레벨 허용:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={configuration.overrides?.sequence?.orderOverridable !== false}
                  onChange={(e) => handleOverridesUpdate(prev => ({
                    ...prev,
                    sequence: { ...prev.sequence, orderOverridable: e.target.checked }
                  }))}
                  className="w-3.5 h-3.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                />
                <span className="text-xs text-gray-700">순서 변경</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={configuration.overrides?.sequence?.optionsOverridable !== false}
                  onChange={(e) => handleOverridesUpdate(prev => ({
                    ...prev,
                    sequence: { ...prev.sequence, optionsOverridable: e.target.checked }
                  }))}
                  className="w-3.5 h-3.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                />
                <span className="text-xs text-gray-700">옵션 편집</span>
              </label>
            </div>
            {configuration.sequence.map((component, index) => (
              <ComponentItem
                key={`${component.type}-${component.location || 'default'}-${index}`}
                component={component}
                index={index}
                onUpdate={handleUpdateComponent}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                isFirst={index === 0}
                isLast={index === configuration.sequence.length - 1}
                isDragging={dragIndex === index}
                dragOverIndex={dragOverIndex}
              />
            ))}
          </div>
        )}

        {/* 학습 진행 탭 */}
        {activeTab === 'rules' && (
          <CompletionRulesEditor
            rules={configuration.completionRules}
            onUpdate={(rules) => setConfiguration(prev => ({ ...prev, completionRules: rules }))}
            overrides={configuration.overrides?.completionRules}
            onOverridesUpdate={(completionRules) => handleOverridesUpdate(prev => ({ ...prev, completionRules }))}
          />
        )}

        {/* 표시 및 기능 탭 */}
        {activeTab === 'settings' && (
          <LearningSettingsEditor
            settings={configuration.learningSettings}
            onUpdate={(settings) => setConfiguration(prev => ({ ...prev, learningSettings: settings }))}
            overrides={configuration.overrides?.learningSettings}
            onOverridesUpdate={(learningSettings) => handleOverridesUpdate(prev => ({ ...prev, learningSettings }))}
          />
        )}

        {/* 상품 레벨 탭 */}
        {activeTab === 'product' && (
          <ProductLearningConfigEditor
            config={configuration.productLearning}
            onChange={(productLearning) => setConfiguration(prev => ({ ...prev, productLearning }))}
            overridable={configuration.overrides?.productLearning}
            onOverridableChange={(productLearning) => handleOverridesUpdate(prev => ({ ...prev, productLearning }))}
          />
        )}

        {/* 모듈 레벨 탭 */}
        {activeTab === 'module' && (
          <ModuleLearningConfigEditor
            config={configuration.moduleLearning}
            onChange={(moduleLearning) => setConfiguration(prev => ({ ...prev, moduleLearning }))}
            overridable={configuration.overrides?.moduleLearning}
            onOverridableChange={(moduleLearning) => handleOverridesUpdate(prev => ({ ...prev, moduleLearning }))}
          />
        )}
      </div>

      {/* 푸터 - onCancel이 있을 때만 표시 */}
      {onCancel && (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
        </div>
      )}

      {/* 미리보기 모달 */}
      {showPreviewModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">Configuration 미리보기</h4>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="p-6">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
                {JSON.stringify(configuration, null, 2)}
              </pre>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
